import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import SupportThread from './SupportThread';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const BLUE      = '#3B82F6';
const FONT_BODY = "'DM Sans', sans-serif";

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18n.t('messaging.now');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

// Pinned "Vocation Works Support" entry + modal thread, embedded in each account-type
// dashboard's Messages tab (or, for Organization, as the entire Messages tab content --
// that dashboard has no general peer messaging today). One component reused identically
// across Worker/Employer/Union/Organization rather than four bespoke inboxes. When no
// conversation exists yet, it doubles as a self-service "Contact Support" entry point.
// openRequest (optional): a counter the parent bumps to open the thread from elsewhere (e.g. the
// delete-account modal's billing message); onOpenRequestHandled lets the parent reset it, so the
// thread doesn't reopen every time this component remounts.
export default function SupportInbox({ supabase, accountType, accountId, viewerId, openRequest = 0, onOpenRequestHandled }) {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState(null);
  const [lastMessage, setLastMessage]   = useState(null);
  const [unreadCount, setUnreadCount]   = useState(0);
  const [showModal, setShowModal]       = useState(false);
  const [contacting, setContacting]     = useState(false);
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 640);
  const contactingRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const refreshSummary = async (convId) => {
    const { data: msgs } = await supabase
      .from('support_messages')
      .select('*')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: false })
      .limit(1);
    setLastMessage(msgs?.[0] || null);

    const { count } = await supabase
      .from('support_messages')
      .select('id', { count: 'exact', head: true })
      .eq('conversation_id', convId)
      .eq('sender_type', 'admin')
      .is('read_at', null);
    setUnreadCount(count || 0);
  };

  const loadConversation = async () => {
    if (!accountId) return;
    const { data } = await supabase
      .from('support_conversations')
      .select('*')
      .eq('account_type', accountType)
      .eq('account_id', accountId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setConversation(data || null);
    if (data) refreshSummary(data.id);
  };

  useEffect(() => {
    loadConversation();
  }, [supabase, accountType, accountId]); // eslint-disable-line

  // New conversations can arrive at any time -- RLS scopes this subscription to rows
  // this account can already see, so no explicit filter is needed here.
  useEffect(() => {
    if (!accountId) return;
    const channel = supabase
      .channel(`support-inbox-conv:${accountType}:${accountId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_conversations' }, () => loadConversation())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [supabase, accountType, accountId]); // eslint-disable-line

  useEffect(() => {
    if (!conversation?.id) return;
    const channel = supabase
      .channel(`support-inbox-msgs:${conversation.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversation.id}` }, () => {
        if (!showModal) refreshSummary(conversation.id);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [supabase, conversation?.id, showModal]); // eslint-disable-line

  // Closing the modal means SupportThread just marked everything read -- resync the badge.
  const handleCloseModal = () => {
    setShowModal(false);
    if (conversation?.id) refreshSummary(conversation.id);
  };

  // Mirrors the admin picker's find-or-create logic (AdminSupportMessages.jsx) so an
  // account never ends up with duplicate open conversations with support.
  const handleContactSupport = async () => {
    if (contactingRef.current) return;
    contactingRef.current = true;
    setContacting(true);

    const { data: existing } = await supabase
      .from('support_conversations')
      .select('*')
      .eq('account_type', accountType)
      .eq('account_id', accountId)
      .eq('status', 'open')
      .maybeSingle();

    let conv = existing;
    if (!conv) {
      const { data: created, error } = await supabase
        .from('support_conversations')
        .insert({ account_type: accountType, account_id: accountId })
        .select('*')
        .single();
      if (error) {
        if (error.code === '23505') {
          // Lost a true-parallel race to the DB's one-open-conversation-per-account
          // constraint -- the other insert won, so just fetch and use that one.
          const { data: winner } = await supabase
            .from('support_conversations')
            .select('*')
            .eq('account_type', accountType)
            .eq('account_id', accountId)
            .eq('status', 'open')
            .maybeSingle();
          conv = winner;
        }
        if (!conv) {
          alert(`${t('support.couldNotStartConversationPrefix')}${error.message}`);
          contactingRef.current = false;
          setContacting(false);
          return;
        }
      } else {
        conv = created;
      }
    }

    setConversation(conv);
    refreshSummary(conv.id);
    setShowModal(true);
    contactingRef.current = false;
    setContacting(false);
  };

  // Same find-or-create path as clicking the entry, so it never duplicates an open conversation.
  useEffect(() => {
    if (!openRequest || !accountId) return;
    onOpenRequestHandled?.();
    handleContactSupport();
  }, [openRequest, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!conversation) {
    return (
      <div
        onClick={handleContactSupport}
        style={{
          display: 'flex', alignItems: 'center', gap: '12px', cursor: contacting ? 'default' : 'pointer',
          backgroundColor: 'rgba(59,130,246,0.08)', borderLeft: `4px solid ${BLUE}`,
          borderRadius: '0 10px 10px 0', padding: '12px 16px', flexShrink: 0,
          fontFamily: FONT_BODY, opacity: contacting ? 0.7 : 1,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          backgroundColor: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '15px',
        }}>
          🛟
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, color: WHITE, marginBottom: '2px' }}>
            {t('support.vocationWorksSupport')}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>
            {contacting ? t('support.startingEllipsis') : t('support.contactSupportPrompt')}
          </div>
        </div>
      </div>
    );
  }

  const preview = lastMessage
    ? (lastMessage.sender_type === 'admin' ? '' : t('messaging.youPrefix')) + lastMessage.content
    : t('messaging.noMessagesYetShort');

  return (
    <>
      <div
        data-support-row
        onClick={() => setShowModal(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer',
          backgroundColor: 'rgba(59,130,246,0.08)', borderLeft: `4px solid ${BLUE}`,
          borderRadius: '0 10px 10px 0', padding: '12px 16px', flexShrink: 0,
          fontFamily: FONT_BODY,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          backgroundColor: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '15px',
        }}>
          🛟
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
            <span style={{ fontSize: '13.5px', fontWeight: 700, color: WHITE }}>
              {t('support.vocationWorksSupport')}
            </span>
            {lastMessage && (
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', flexShrink: 0, marginLeft: '8px' }}>
                {timeAgo(lastMessage.created_at)}
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {preview}
          </div>
        </div>
        {unreadCount > 0 && (
          <span data-support-unread style={{
            backgroundColor: AMBER, color: NAVY,
            borderRadius: '999px', fontSize: '11px', fontWeight: 700,
            padding: '2px 7px', lineHeight: 1.4, flexShrink: 0,
          }}>
            {unreadCount}
          </span>
        )}
      </div>

      {showModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) handleCloseModal(); }}
          style={{
            // Phone: the thread fills the screen above the dashboard's bottom nav (58px + safe
            // area, the same in every dashboard that hosts this), so the composer sits above it.
            position: 'fixed', inset: 0, zIndex: 1000,
            bottom: isMobile ? 'calc(58px + env(safe-area-inset-bottom))' : 0,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '16px',
          }}
        >
          <div style={{
            backgroundColor: NAVY, width: '100%',
            maxWidth: isMobile ? '100%' : '460px',
            height: isMobile ? '100%' : '640px',
            maxHeight: isMobile ? '100%' : '85vh',
            borderRadius: isMobile ? 0 : '14px',
            border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.12)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <SupportThread
              supabase={supabase}
              conversationId={conversation.id}
              viewerType="account"
              viewerId={viewerId}
              title={t('support.vocationWorksSupport')}
              subtitle={conversation.status === 'closed' ? t('support.statusClosed') : t('support.repliesWithinDayNote')}
              avatarLabel="🛟"
              avatarColor={BLUE}
              onBack={handleCloseModal}
            />
          </div>
        </div>
      )}
    </>
  );
}
